import { parseOptionalDate, formatAppDate } from '@/utils/date';
import { isAutogeneratedRow, GridColDef } from '@mui/x-data-grid-premium';
import { RealizedGainLossRow } from '../realizedGainLoss';
import { UnrealizedGainLossRow } from '../unrealizedGainLoss';

const GainsLossesUtility = () => {
  type GainsLossRow = UnrealizedGainLossRow | RealizedGainLossRow;

  // Grouping rows and rolled-up multi-lot rows have no single date to show.
  const isMultiLotDateRow = (row: GainsLossRow): boolean =>
    isAutogeneratedRow(row) || row.NumberOfTaxLots > 1;

  /**
   * Builds a date column whose value stays a `Date` and whose display text comes
   * from the formatter. Ensures more robust date sorting comparators.
   *
   * `valueFormatter` only modifies the visual presentation. Internal features like sorting and filtering
   * will continue to process the raw, underlying Date object accurately.
   *
   * `valueGetter` modifies the core data value. Converting a date to a string here will
   * break the built-in native date-sorting algorithms.
   */
  const createDateColumn = ({
    field,
    headerName,
    multipleLabel,
  }: {
    field: 'OpenDate' | 'TradeDate' | 'SaleDate';
    headerName: string;
    multipleLabel?: string;
  }): GridColDef<GainsLossRow> => ({
    field,
    headerName,
    flex: 1,
    type: 'date',
    align: 'right',
    headerAlign: 'right',
    valueGetter: (value: string | undefined, row) => {
      if (isMultiLotDateRow(row)) {
        return null;
      }

      return parseOptionalDate(value);
    },
    valueFormatter: (value: Date | null, row) => {
      if (isMultiLotDateRow(row)) {
        return multipleLabel ?? 'Multiple';
      }

      return value ? formatAppDate(value) : '';
    },
  });

  return { createDateColumn };
};

export default GainsLossesUtility();
